<script setup lang="ts">
    import { onMounted } from 'vue';
    import List from '@shared/components/List/List.vue';
    import ListItem from './ListItem.vue';
    import { useTextMessageList } from '../SmsList.service';
    import { deleteSelected, abortSending, changeStatus } from '../TextMessages.access';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import PrePaid from '../Shared/PrePaid.vue';
    import { type Status, type TextMessageStatus } from '../TextMessagesTypes';
    import { createNewTemplate } from '../TextMessages.access';
    import { UserEventService } from '@shared/Root/UserEvent.service';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { inject } from 'vue';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const userEventService = injectorAccess.injector.get(UserEventService);

    const { state, switchTab, filterOptions, orderOptions, filteredItems, clearSelection, disabledCheckboxes, initialize, dateRangeChanged } = useTextMessageList();

    onMounted(async () => {
        await initialize();
    });

    const handleChangeStatus = (data: { id: string; status: TextMessageStatus }) => {
        if (state.isBusy) return;
        state.isBusy = true;

        changeStatus(data.id, data.status).finally(() => {
            state.isBusy = false;
            switchTab('drafts');
        });
    };

    const handleAbortSending = (id: string) => {
        if (state.isBusy) return;
        state.isBusy = true;

        abortSending(id)
            .then(() => {
                bsAlert({ title: $Resources.AlertSmsAbortedHeader, content: $Resources.AlertSmsAbortedBody });
            })
            .catch(function () {
                bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorAbortingSmsHeader, content: $Resources.ErrorAbortingSmsBody });
            })
            .finally(() => {
                state.isBusy = false;
                switchTab('drafts');
            });
    };

    const onDeleteSelected = () => {
        if (state.isBusy) return;
        state.isBusy = true;

        let list = state.selected.map((id: string) => {
            return {
                SmsMessageId: id,
            };
        });

        deleteSelected(list)
            .then(() => {
                state.selected.forEach((id: string) => {
                    let found = state.items.find((item) => {
                        return id === item.SmsMessageId;
                    });

                    found && (found.Deleted = true);
                });

                state.selected = [];
                bsAlert({ title: $Resources.AlertSmsDeletedHeading, content: $Resources.AlertSmsDeletedBody });
            })
            .catch(() => {
                bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorDeletingSmsHeading, content: $Resources.ErrorDeletingSmsBody });
            })
            .finally(() => {
                state.isBusy = false;
            });
    };

    const onCreateNewTextMessageTemplate = async () => {
        if (state.isBusy || !state.hasScope) return;
        state.isBusy = true;

        try {
            const textMessage = await createNewTemplate();
            userEventService.registerUserEvent('SMS', 'Created template sms', '');
            document.location = '/TextMessages/' + textMessage.SmsMessageId;
        } catch (error) {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorCreatingIssueHeading, content: $Resources.ErrorCreatingIssueBody });
            state.isBusy = false;
        }
    };
</script>

<template>
    <List
        v-if="state.tabData"
        itemId="SmsMessageId"
        :title="$Resources.SmsMenuItem"
        :description="state.tabData.description"
        :items="filteredItems()"
        :tabs="state.tabs"
        :currentTab="state.currentTab"
        :loading="state.isBusy"
        :filterKeys="filterOptions()"
        :orderOptions="orderOptions()"
        :checkedItems="state.selected"
        :disableCheckboxes="state.currentTab === 'paused' || state.currentTab === 'scheduled' || state.currentTab === 'sending'"
        :disabledCheckboxes="disabledCheckboxes"
        :dateFilter="state.dateFilter"
        @update:tab="(newVal) => switchTab(newVal as Status)"
        @update:checkedItems="(newVal) => (state.selected = newVal as string[])"
        @clearSelected="clearSelection"
        @deleteSelected="onDeleteSelected"
        @onDateRangeChange="dateRangeChanged">
        <template #header>
            <PrePaid></PrePaid>
        </template>
        <template #toolbar>
            <template v-if="state.hasScope">
                <router-link :to="{ name: 'NewTextMessage' }" class="btn btn-default" v-show="state.selected.length === 0">
                    <span class="glyphicon glyphicon-plus m-xxs-r"></span>
                    {{ $Resources.SmsNewButton }}
                </router-link>

                <button
                    type="button"
                    v-if="state.currentTab === 'template'"
                    @click="onCreateNewTextMessageTemplate"
                    class="btn btn-default"
                    v-show="state.selected.length === 0">
                    <span class="glyphicon glyphicon-plus m-xxs-r"></span>
                    {{ $Resources.CreateTemplateButton }}
                </button>
            </template>
        </template>
        <template #item="{ item, index }">
            <ListItem
                :textMessage="item"
                :currentTab="state.currentTab"
                @abortSending="(id: string) => handleAbortSending(id)"
                @update:status="(data: { id: string; status: TextMessageStatus }) => handleChangeStatus(data)"></ListItem>
        </template>
    </List>
</template>
